/**
 * health-strip.tsx — Workspace health counters panel.
 *
 * Renders 4 stat tiles:
 *   1. Total packages
 *   2. Blocked count (lifecycle === 'blocked' OR trust.state === 'blocked' OR completeness.state === 'has-failed')
 *   3. Pending approvals (reviewer assigned but no recent approval)
 *   4. Active reviews (packages where current user is reviewer)
 *
 * Props are computed by HomeDashboard from the enriched list response and
 * passed down — this component is pure/presentational.
 */

import { useTranslation } from 'react-i18next'
import { cn } from '../../lib/utils'
import { useAppStore } from '../../stores/app-store'
import { Package, Ban, Clock, Eye } from 'lucide-react'

// ── Types ─────────────────────────────────────────────────────────────────────

export interface HealthStripProps {
  totalPackages: number
  blockedCount: number
  pendingApprovals: number
  activeReviews: number
}

// ── Sub-component: single stat tile ──────────────────────────────────────────

interface StatTileProps {
  label: string
  value: number
  icon: React.ReactNode
  /** When true, applies an accent colour to draw attention. */
  accent?: boolean
  isDark: boolean
}

function StatTile({ label, value, icon, accent = false, isDark }: StatTileProps) {
  return (
    <div
      className={cn(
        'flex flex-1 flex-col gap-1 rounded-xl border px-4 py-3',
        isDark
          ? 'border-white/[0.06] bg-surface-1'
          : 'border-neutral-200 bg-white',
        accent && value > 0
          ? isDark
            ? 'border-red-500/30 bg-red-500/10'
            : 'border-red-200 bg-red-50'
          : '',
      )}
    >
      <div className="flex items-center gap-1.5">
        <span
          className={cn(
            'flex h-5 w-5 items-center justify-center',
            accent && value > 0
              ? isDark ? 'text-red-400' : 'text-red-600'
              : isDark ? 'text-neutral-500' : 'text-neutral-400',
          )}
          aria-hidden="true"
        >
          {icon}
        </span>
        <span
          className={cn(
            'text-[11px] font-medium uppercase tracking-wide',
            isDark ? 'text-neutral-500' : 'text-neutral-500',
          )}
        >
          {label}
        </span>
      </div>
      <p
        className={cn(
          'text-2xl font-semibold tabular-nums',
          accent && value > 0
            ? isDark ? 'text-red-300' : 'text-red-700'
            : isDark ? 'text-neutral-100' : 'text-neutral-900',
        )}
      >
        {value}
      </p>
    </div>
  )
}

// ── Component ─────────────────────────────────────────────────────────────────

export function HealthStrip({ totalPackages, blockedCount, pendingApprovals, activeReviews }: HealthStripProps) {
  const { t } = useTranslation('dashboard')
  const { theme } = useAppStore()
  const isDark = theme === 'dark'

  return (
    <section aria-label={t('health.title')}>
      <div className="flex flex-wrap gap-3">
        <StatTile
          label={t('health.total')}
          value={totalPackages}
          icon={<Package className="h-3.5 w-3.5" />}
          isDark={isDark}
        />
        <StatTile
          label={t('health.blocked')}
          value={blockedCount}
          icon={<Ban className="h-3.5 w-3.5" />}
          accent
          isDark={isDark}
        />
        <StatTile
          label={t('health.pendingApprovals')}
          value={pendingApprovals}
          icon={<Clock className="h-3.5 w-3.5" />}
          isDark={isDark}
        />
        <StatTile
          label={t('health.activeReviews')}
          value={activeReviews}
          icon={<Eye className="h-3.5 w-3.5" />}
          isDark={isDark}
        />
      </div>
    </section>
  )
}
